/**
 * Cloud builds — HTTP adapter against the Cryptarch Worker API.
 *
 * Mirrors the function-level shape of `src/domain/builds.ts` (listBuilds,
 * saveBuild, deleteBuild, …) so the router-level facade in
 * `src/domain/repository.ts` can swap between local IDB and cloud without
 * any caller refactor.
 *
 * Auth: every request sends `Authorization: Bearer <access_token>` pulled
 * from `$api/auth.getAccessToken()`. 401 → token expired → bubble up so
 * the caller can re-login.
 *
 * Returns the same `SavedBuild` shape that the IDB layer returns, so the
 * Build sidebar and Wizard load flow don't care whether they're looking
 * at a local or cloud build.
 */

import { getAccessToken } from '$api/auth';
import { trackEvent } from '$api/telemetry';
import type { SavedBuild } from './builds';
import type { ArmorClass } from './armor';
import type { WizardState } from './wizard';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

/** True when the client is configured to talk to the API. False makes
 *  every function in this module throw — the facade short-circuits on
 *  this so cloud calls never escape into IDB-only mode. */
export function cloudConfigured(): boolean {
  return API_BASE.length > 0;
}

interface CloudBuildDTO {
  id: string;
  userId: string;
  characterClass: ArmorClass;
  name: string;
  description: string;
  category: string | null;
  subclassElement: string | null;
  exoticHash: number | null;
  state: Record<string, unknown>;
  createdAt: number;
  updatedAt: number;
  version: number;
  shareToken: string | null;
  isShared: boolean;
  likeCount: number;
  viewCount: number;
}

/** Augmented SavedBuild that carries cloud-only fields. The IDB shape
 *  doesn't have these; consumers that depend on them must branch on
 *  `'version' in build`. */
export interface CloudSavedBuild extends SavedBuild {
  version: number;
  shareToken: string | null;
  isShared: boolean;
  likeCount: number;
  viewCount: number;
  category: string | null;
  description: string;
}

function dtoToSavedBuild(dto: CloudBuildDTO): CloudSavedBuild {
  return {
    id: dto.id,
    name: dto.name,
    savedAt: new Date(dto.updatedAt).toISOString(),
    state: dto.state as CloudSavedBuild['state'],
    version: dto.version,
    shareToken: dto.shareToken,
    isShared: dto.isShared,
    likeCount: dto.likeCount,
    viewCount: dto.viewCount,
    category: dto.category,
    description: dto.description,
  };
}

async function authedFetch(path: string, init?: RequestInit): Promise<Response> {
  if (!cloudConfigured()) {
    throw new Error('cloud API not configured (VITE_API_BASE_URL is empty)');
  }
  const token = await getAccessToken();
  if (!token) throw new Error('not authenticated');
  const headers = new Headers(init?.headers);
  headers.set('authorization', `Bearer ${token}`);
  if (init?.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

async function readJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // non-JSON error body
    }
    const message =
      body &&
      typeof body === 'object' &&
      'error' in body &&
      typeof (body as { error: unknown }).error === 'string'
        ? (body as { error: string }).error
        : `${res.status} ${res.statusText}`;
    const err = new Error(`cryptarch-api: ${message}`) as Error & {
      status?: number;
      body?: unknown;
    };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return res.json() as Promise<T>;
}

// ── lifecycle ────────────────────────────────────────────────────────────

/** Confirm the user exists server-side + touch last_seen. Cheap; safe to
 *  call once per session after login. */
export async function whoami(): Promise<{
  membershipId: string;
  displayName: string;
  isAdmin: boolean;
}> {
  const res = await authedFetch('/v1/auth/whoami', { method: 'POST' });
  return readJson(res);
}

// ── CRUD ─────────────────────────────────────────────────────────────────

export interface CloudListFilter {
  characterClass?: ArmorClass;
  category?: string;
}

export async function listCloudBuilds(filter?: CloudListFilter): Promise<CloudSavedBuild[]> {
  const qs = new URLSearchParams();
  if (filter?.characterClass) qs.set('character_class', filter.characterClass);
  if (filter?.category) qs.set('category', filter.category);
  const path = `/v1/builds${qs.toString() ? `?${qs}` : ''}`;
  const res = await authedFetch(path);
  const body = await readJson<{ builds: CloudBuildDTO[] }>(res);
  return body.builds.map(dtoToSavedBuild);
}

export async function getCloudBuild(id: string): Promise<CloudSavedBuild | null> {
  const res = await authedFetch(`/v1/builds/${encodeURIComponent(id)}`);
  if (res.status === 404) return null;
  const dto = await readJson<CloudBuildDTO>(res);
  return dtoToSavedBuild(dto);
}

export interface CloudCreateInput {
  characterClass: ArmorClass;
  name: string;
  description?: string;
  category?: string | null;
  subclassElement?: string | null;
  exoticHash?: number | null;
  state: Omit<WizardState, 'step' | 'completed'>;
  /** Optional client-side UUID; lets optimistic UI know the id pre-roundtrip. */
  id?: string;
}

export async function createCloudBuild(input: CloudCreateInput): Promise<CloudSavedBuild> {
  const res = await authedFetch('/v1/builds', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  const dto = await readJson<CloudBuildDTO>(res);
  void trackEvent({
    kind: 'share_create',
    meta: {
      category: input.category ?? null,
      hasExotic: input.exoticHash != null,
    },
  });
  return dtoToSavedBuild(dto);
}

export interface CloudPatchInput {
  version: number;
  name?: string;
  description?: string;
  category?: string | null;
  subclassElement?: string | null;
  exoticHash?: number | null;
  state?: Omit<WizardState, 'step' | 'completed'>;
}

export async function patchCloudBuild(
  id: string,
  patch: CloudPatchInput,
): Promise<CloudSavedBuild> {
  const res = await authedFetch(`/v1/builds/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  const dto = await readJson<CloudBuildDTO>(res);
  return dtoToSavedBuild(dto);
}

export async function deleteCloudBuild(id: string): Promise<void> {
  const res = await authedFetch(`/v1/builds/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 404) {
    // 404 is fine — idempotent delete. Anything else is a real failure.
    await readJson(res);
  }
}

export async function setCloudShare(
  id: string,
  enabled: boolean,
): Promise<{ shareToken: string | null }> {
  const res = await authedFetch(`/v1/builds/${encodeURIComponent(id)}/share`, {
    method: 'POST',
    body: JSON.stringify({ enabled }),
  });
  return readJson(res);
}

// ── public share view (no auth) ──────────────────────────────────────────

export async function getSharedBuild(token: string): Promise<CloudSavedBuild | null> {
  if (!cloudConfigured()) return null;
  const res = await fetch(`${API_BASE}/v1/share/${encodeURIComponent(token)}`);
  if (res.status === 404) return null;
  const dto = await readJson<CloudBuildDTO>(res);
  return dtoToSavedBuild(dto);
}

// ── likes ────────────────────────────────────────────────────────────────

export async function likeCloudBuild(id: string): Promise<{ likeCount: number }> {
  const res = await authedFetch(`/v1/builds/${encodeURIComponent(id)}/like`, { method: 'POST' });
  return readJson(res);
}

export async function unlikeCloudBuild(id: string): Promise<{ likeCount: number }> {
  const res = await authedFetch(`/v1/builds/${encodeURIComponent(id)}/like`, { method: 'DELETE' });
  return readJson(res);
}
